import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Port } from '../AISMapContext';
import { GeoJson } from '@ais-ui/api-spec/shapes';

// Supported feature types for context menu
export type MapFeatureType = 'port' | 'vessel' | 'shape' | 'aton' | 'source-identifier' | 'gps-lost' | 'coordinate';

// Base interface for all map features
interface BaseMapFeature {
  type: MapFeatureType;
}

// Port feature data
export interface PortFeature extends BaseMapFeature {
  type: 'port';
  data: Port;
}

// Vessel feature data (for future use)
export interface VesselFeature extends BaseMapFeature {
  type: 'vessel';
  data: {
    mmsi: string;
    name?: string;
    imo?: string;
  };
}

// Shape feature data - includes full GeoJson for editing and area calculation
export interface ShapeFeature extends BaseMapFeature {
  type: 'shape';
  data: GeoJson;
}

// Coordinate feature data - for copying map coordinates
export interface CoordinateFeature extends BaseMapFeature {
  type: 'coordinate';
  data: {
    /** Longitude in decimal degrees (EPSG:4326) */
    longitude: number;
    /** Latitude in decimal degrees (EPSG:4326) */
    latitude: number;
  };
}

// Union type of all feature types
export type MapFeature = PortFeature | VesselFeature | ShapeFeature | CoordinateFeature;

interface MapContextMenuState {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  feature: MapFeature | null;
}

// State for shape editing dialog (persists after context menu closes)
interface EditShapeDialogState {
  isOpen: boolean;
  shape: GeoJson | null;
}

interface MapContextMenuContextValue {
  state: MapContextMenuState;
  openContextMenu: (position: { x: number; y: number }, feature: MapFeature) => void;
  closeContextMenu: () => void;
  editShapeDialog: EditShapeDialogState;
  openEditShapeDialog: (shape: GeoJson) => void;
  closeEditShapeDialog: () => void;
}

const MapContextMenuContext = createContext<MapContextMenuContextValue | undefined>(undefined);

interface MapContextMenuProviderProps {
  children: ReactNode;
}

export const MapContextMenuProvider = ({ children }: MapContextMenuProviderProps) => {
  const [state, setState] = useState<MapContextMenuState>({
    isOpen: false,
    position: null,
    feature: null,
  });

  const [editShapeDialog, setEditShapeDialog] = useState<EditShapeDialogState>({
    isOpen: false,
    shape: null,
  });

  const openContextMenu = useCallback(
    (position: { x: number; y: number }, feature: MapFeature) => {
      setState({
        isOpen: true,
        position,
        feature,
      });
    },
    [],
  );

  const closeContextMenu = useCallback(() => {
    setState({
      isOpen: false,
      position: null,
      feature: null,
    });
  }, []);

  const openEditShapeDialog = useCallback((shape: GeoJson) => {
    setEditShapeDialog({
      isOpen: true,
      shape,
    });
  }, []);

  const closeEditShapeDialog = useCallback(() => {
    setEditShapeDialog({
      isOpen: false,
      shape: null,
    });
  }, []);

  const value = useMemo(
    () => ({
      state,
      openContextMenu,
      closeContextMenu,
      editShapeDialog,
      openEditShapeDialog,
      closeEditShapeDialog,
    }),
    [state, openContextMenu, closeContextMenu, editShapeDialog, openEditShapeDialog, closeEditShapeDialog],
  );

  return (
    <MapContextMenuContext.Provider value={value}>
      {children}
    </MapContextMenuContext.Provider>
  );
};

export const useMapContextMenu = () => {
  const context = useContext(MapContextMenuContext);
  if (!context) {
    throw new Error('useMapContextMenu must be used within a MapContextMenuProvider');
  }
  return context;
};
